import { useState } from 'react'
import KeyboardArrowDownOutlinedIcon from '@mui/icons-material/KeyboardArrowDownOutlined'
import KeyboardArrowUpOutlinedIcon from '@mui/icons-material/KeyboardArrowUpOutlined'
import { useFirestoreGeneral, useFirestoreGeneralOrderBy } from '../../../firebase/useFirestore'
import Location from '../../../hooks/Location'
import useEnsureAdressLocation from '../../../hooks/useEnsureAdressLocation'
import useAdressExternalData from '../../../hooks/useAdressExternalData'
import { selectCbsMetrics, CBS_KERNCIJFERS_YEAR } from '../../../helpers/cbs/cbsQueries'
import { selectBagFacts } from '../../../helpers/bag/bagQueries'
import { selectEnergyLabelFacts } from '../../../helpers/energylabel/energyLabelQueries'
import buildExternalDataSections from '../../../helpers/adressHelpers/externalDataSections'
import { addressMismatchMessage } from '../../../helpers/pdok/verifyAddressMatch'

const nl = (value) => (typeof value === 'number' ? value.toLocaleString('nl-NL') : value)

const formatExternalValue = (value, unit) => {
    if (unit === 'year') return String(value)
    if (unit === 'date') return new Date(value).toLocaleDateString('nl-NL')
    if (unit === '%') return `${nl(value)}%`
    if (unit === 'x1000euro') return typeof value === 'number' ? `€ ${(value * 1000).toLocaleString('nl-NL')}` : value
    if (unit === 'm³') return `${nl(value)} m³`
    if (unit === 'kWh') return `${nl(value)} kWh`
    if (unit === 'inwoners/km²') return `${nl(value)} inw./km²`
    return nl(value)
}

const DataRow = ({ label, value }) => (
    <li className="adress-dashboard-data-row">
        <span className="adress-dashboard-data-label">{label}</span>
        <span className="adress-dashboard-data-value">{value}</span>
    </li>
)

const ExternalDataCard = () => {
    const adressId = Location()[3]

    // Alleen wat de gebruiker ZELF heeft aan- of uitgeklapt. Zolang hij niets
    // deed, wint `defaultOpen` van de sectie. Geen useEffect die de secties in
    // state kopieert: die zou bij elke nieuwe snapshot zijn keuze overschrijven.
    // AI-CONTEXT: See docs/pitfalls.md#state-draft-wiped-by-firestore-snapshot
    const [toggled, setToggled] = useState({})

    const adressList = useFirestoreGeneral('Adresses', 'ID', adressId || '')
    const adress = Array.isArray(adressList) && adressList.length > 0 ? adressList[0] : null

    const personaData = useFirestoreGeneralOrderBy('PersonaData', 'PersonaID', adressId || '', 'Position', 'asc')

    const { location, status: locationStatus, ...controle } = useEnsureAdressLocation(adress, personaData)
    const { cbs, bag, energyLabel, energyLabelStatus, status } = useAdressExternalData(location)

    const bagFacts = selectBagFacts(bag)
    const cbsGroups = selectCbsMetrics(cbs)
    const energyLabelFacts = selectEnergyLabelFacts(energyLabel)
    const hasData = bagFacts.length > 0 || cbsGroups.length > 0 || energyLabelFacts.length > 0

    // De indeling zelf zit in een helper: die beantwoordt 'hoe tonen we dit aan
    // iemand die met energiearmoede bezig is', en dat is een andere vraag dan
    // 'wat geeft deze bron'.
    const sections = buildExternalDataSections({ energyLabelFacts, bagFacts, cbsGroups })

    /** Open staat de sectie zoals de gebruiker hem zette, anders zoals hij bedoeld is. */
    const isOpen = (section) => (
        Object.prototype.hasOwnProperty.call(toggled, section.id)
            ? toggled[section.id]
            : section.defaultOpen
    )

    const toggle = (section) => setToggled((prev) => ({ ...prev, [section.id]: !isOpen(section) }))

    /** Waarom er geen energielabel staat; dat is zelf ook informatie. */
    const energyLabelNotice = () => {
        if (energyLabelFacts.length > 0) return null
        return (
            <p className="adress-external-data-empty">
                {energyLabelStatus === 'loading' && 'Energielabel ophalen…'}
                {energyLabelStatus === 'error' && 'Energielabel kon niet worden opgehaald.'}
                {energyLabelStatus === 'idle' && 'Geen energielabel beschikbaar — geen BAG-koppeling voor dit adres.'}
                {energyLabelStatus === 'ready' && 'Geen energielabel geregistreerd voor dit adres.'}
            </p>
        )
    }

    const renderBody = () => {
        if (locationStatus === 'resolving') return <p className="adress-external-data-empty">Locatie ophalen…</p>
        if (locationStatus === 'missing-input') {
            return (
                <p className="adress-external-data-empty">
                    Geen locatie bekend — vul Postcode + Huisnummer in om externe gegevens te tonen.
                </p>
            )
        }
        if (locationStatus === 'failed') {
            // Zeg wát er mis is: 'niet gevonden' is iets anders dan 'PDOK vond een
            // ander adres bij deze postcode en dit huisnummer', en dat weer iets
            // anders dan 'hier staan meerdere woningen, welke?'.
            const melding = addressMismatchMessage(controle)
            return (
                <p className="adress-external-data-empty" role="alert">
                    {melding || 'Locatie niet gevonden, externe gegevens niet beschikbaar.'}
                </p>
            )
        }
        // Het pand is bekend, de woning niet (meerdere woningen of een onbekende
        // toevoeging): de buurtcijfers kloppen wél, dus die blijven, met de
        // melding erboven. Energielabel en woningfeiten horen bij één woning.
        const unitNotice = locationStatus === 'unit-unknown'
            ? <p className="adress-external-data-empty" role="alert">{addressMismatchMessage(controle)}</p>
            : null
        if (status === 'loading' || status === 'idle') {
            return <>{unitNotice}<p className="adress-external-data-empty">Externe gegevens ophalen…</p></>
        }
        if (status === 'error') {
            return <>{unitNotice}<p className="adress-external-data-empty">Externe gegevens konden niet worden opgehaald.</p></>
        }
        if (!hasData && energyLabelStatus !== 'loading' && energyLabelStatus !== 'error') {
            return <>{unitNotice}<p className="adress-external-data-empty">Geen externe gegevens beschikbaar voor dit adres.</p></>
        }

        return (
            <>
                {unitNotice}
                {sections.length === 0 ? energyLabelNotice() : null}
                {sections.map((section) => {
                    const open = isOpen(section)
                    return (
                        <div key={section.id} className="adress-external-data-section">
                            {/* Een knop en geen kop: de titel was hier nooit een
                                <h*>, dus er gaat geen kop verloren. En een echte
                                <button> in plaats van een klikbare <div>, zodat
                                het met het toetsenbord ook werkt. */}
                            <button
                                type="button"
                                className="adress-external-data-group-title"
                                aria-expanded={open}
                                onClick={() => toggle(section)}
                            >
                                <span>{section.title}</span>
                                {open
                                    ? <KeyboardArrowUpOutlinedIcon fontSize="small" />
                                    : <KeyboardArrowDownOutlinedIcon fontSize="small" />}
                            </button>
                            {open && (
                                <>
                                    <ul className="adress-dashboard-data-list">
                                        {section.facts.map((fact) => (
                                            <DataRow
                                                key={fact.key || fact.label}
                                                label={fact.label}
                                                value={formatExternalValue(fact.value, fact.unit)}
                                            />
                                        ))}
                                    </ul>
                                    {section.id === 'woning' ? energyLabelNotice() : null}
                                </>
                            )}
                        </div>
                    )
                })}
                <p className="adress-external-data-source">
                    Bron: CBS Kerncijfers wijken en buurten {CBS_KERNCIJFERS_YEAR}
                    {location?.BuurtCode ? ` (buurt ${location.BuurtCode})` : ''} · Kadaster BAG · RVO EP-Online
                </p>
            </>
        )
    }

    return (
        <div className="new-dashboard-card adress-external-data">
            <div className="new-dashboard-card-content">
                <h3 className="new-dashboard-card-title">Externe data</h3>
                {renderBody()}
            </div>
        </div>
    )
}

export default ExternalDataCard
